(ns ui.enrich.model
  "Paths and pure state transformers for the AI enrichment client.

   A user may run several jobs at once, and a job outlives the screen that
   started it: the top bar's process list shows every job of the account until
   its result is saved or discarded, across reloads (the jobs are durable on the
   backend). State is plain data in app-db:

   - :requests   sent requests without a job id yet, keyed by the token the
                 starting screen chose: {:token :status :request-context}. A
                 request that fails stays here as :ai.status/failed until the
                 user dismisses it, since no job exists to list.
   - :jobs       the account's unresolved jobs keyed by result id: the backend's
                 job summary, plus :token when this session started it, and the
                 suggestions once fetched (:loaded? true). :fetch-failed? marks
                 one whose fetch failed, until it is fetched again.
   - :reviewing  the result id whose review is open.
   - :resolved   the result ids resolved in this session: a reply that was
                 already on its way does not bring one back.
   - :list-gen   counts the list requests. A job records the count it was
                 added at (:added-gen), so a list asked for before it existed
                 does not drop it.
   - :fetches    {result id -> the id of its latest result fetch}, also for a
                 job not held yet: an older fetch's reply is dropped.
   - :survey-applied  {survey process id -> result id}: a survey job whose
                 answers are in the editor but not saved yet. It is resolved
                 when they are saved or the editor is left without saving.
   - :menu       the process list: {:open? :highlight :focus},
                 highlight being the job that just finished. Starting a job
                 opens the list on that job alone (:focus, its token) once the
                 flying robot has landed on the list's button.

   A screen that starts a job follows it by its token, so it only reacts to its
   own job while others run."
  (:require
   [common.endpoints.ai-enrichment :as models]))

(declare open-menu-on remove-job)

(def root-path [::state])
(def requests-path (conj root-path :requests))
(def jobs-path (conj root-path :jobs))
(def reviewing-path (conj root-path :reviewing))
(def menu-path (conj root-path :menu))
(def survey-applied-path (conj root-path :survey-applied))
(def resolved-path (conj root-path :resolved))
(def list-gen-path (conj root-path :list-gen))
(def fetches-path (conj root-path :fetches))

(def listener-id
  "Single standing completion listener id for enrichment (registering again
   with the same id replaces it, so re-entry is safe)."
  "ai-job-enrichment")

(def in-progress-statuses
  #{:ai.status/pending :ai.status/processing})

(def failed-statuses
  "Terminal statuses that mean the job produced no usable suggestions."
  #{:ai.status/failed :ai.status/token-exhausted})

(defn in-progress-status? [status] (contains? in-progress-statuses status))
(defn failed-status? [status] (contains? failed-statuses status))

(def failure-reason->tr-key
  "Localization key of the banner for a specific failure reason. A reason that
   is absent or not here falls back to the generic failed copy."
  {:ai.failure-reason/not-enabled    :enrich/failure-not-enabled
   :ai.failure-reason/no-service     :enrich/failure-no-service
   :ai.failure-reason/no-template    :enrich/failure-no-template
   :ai.failure-reason/access-lost    :enrich/failure-access-lost
   :ai.failure-reason/provider-error :enrich/failure-provider-error})

(defn failure-tr-key
  "The localization key for `job`'s failure banner: the reason-specific key, or
   `fallback` when the reason is absent or unknown."
  [job fallback]
  (get failure-reason->tr-key (:failure-reason job) fallback))

;; ---------------------------------------------------------------------------
;; What a job is about
;; ---------------------------------------------------------------------------

(defn job-kind
  "What the job fills, from its frozen request context:
   - :survey        survey answers, applied inline by the survey editor
   - :existing      an existing entity, reviewed old-vs-new
   - :tp-create     a transaction process create modal
   - :config-create a configurable creation curtain"
  [{:keys [request-context]}]
  (let [{:keys [entity-type template-kw draft]} request-context]
    (cond
      (= :entity.type/survey-process entity-type) :survey
      (:entity-id draft)                          :existing
      template-kw                                 :tp-create
      :else                                       :config-create)))

(defn job-entity-id
  "The existing entity a job fills, or nil."
  [job]
  (-> job :request-context :draft :entity-id))

(defn job-survey-process-id
  "The survey process a survey job answers, or nil."
  [job]
  (-> job :request-context :draft :survey-process-id))

;; ---------------------------------------------------------------------------
;; Requests and jobs
;; ---------------------------------------------------------------------------

(defn requests [db] (get-in db requests-path))
(defn jobs [db] (get-in db jobs-path))
(defn job [db result-id] (get-in db (conj jobs-path result-id)))

(defn add-request
  "A request was sent; follow it by `token` until the job id comes back."
  [db token request-context]
  (assoc-in db (conj requests-path token)
            {:token           token
             :status          :ai.status/pending
             :request-context request-context}))

(defn request-failed
  [db token]
  (assoc-in db (conj requests-path token :status) :ai.status/failed))

(defn dismiss-request
  [db token]
  (update-in db requests-path dissoc token))

(defn- resolved?
  [db result-id]
  (contains? (get-in db resolved-path) result-id))

(defn list-gen [db] (get-in db list-gen-path 0))

(defn list-requested
  "A list request starts: later jobs are newer than its reply."
  [db]
  (update-in db list-gen-path (fnil inc 0)))

(defn- added
  "`job` as held after an update, stamped with the list count when it is new."
  [db held job]
  (cond-> job
    (nil? held) (assoc :added-gen (list-gen db))))

(defn merge-jobs
  "Add the jobs of the backend's list. A job this session already holds keeps
   what it knows beyond the summary (its token and fetched suggestions). It
   takes the listed status unless its own is already terminal; a terminal one
   only takes what it lacks, such as the creation time when its result was
   fetched before the request's reply. A job resolved in this session is not
   added back."
  [db summaries]
  (reduce (fn [db {:keys [result-id] :as summary}]
            (if (resolved? db result-id)
              db
              (update-in db (conj jobs-path result-id)
                         (fn [held]
                           (added db held
                                  (if (and held (not (in-progress-status? (:status held))))
                                    (merge summary held)
                                    (merge held summary)))))))
          db
          summaries))

(defn request-accepted
  "The backend created the job: it moves from the requests to the jobs. A job
   can finish before this reply arrives, so one already held keeps its terminal
   status and fetched suggestions (see merge-jobs)."
  [db token job-summary]
  (cond-> (-> db
              (update-in requests-path dissoc token)
              (merge-jobs [job-summary]))
    ;; Resolved before this reply came, it stays gone.
    (not (resolved? db (:result-id job-summary)))
    (assoc-in (conj jobs-path (:result-id job-summary) :token) token)))

(defn job-loaded
  "Store a fetched job (with its suggestions), unless it was resolved in this
   session while the fetch was on its way. A snapshot taken while the job ran
   does not replace a finished one that arrived first."
  [db {:keys [result-id] :as fetched}]
  (if (resolved? db result-id)
    db
    (update-in db (conj jobs-path result-id)
               (fn [held]
                 (if (and held
                          (not (in-progress-status? (:status held)))
                          (in-progress-status? (:status fetched)))
                   held
                   (added db held (-> (merge held fetched {:loaded? true})
                                      (dissoc :fetch-failed?))))))))

(defn fetch-started
  "A fetch `fetch-id` of the job's result starts: replies of earlier fetches
   are dropped."
  [db result-id fetch-id]
  (assoc-in db (conj fetches-path result-id) fetch-id))

(defn current-fetch?
  "Whether `fetch-id` is the latest fetch of the job's result."
  [db result-id fetch-id]
  (= fetch-id (get-in db (conj fetches-path result-id))))

(defn reconcile-jobs
  "Take the backend's list of unresolved jobs, requested at list count `gen`.
   A held job missing from it was resolved elsewhere, such as in another tab,
   and goes, unless it was added after the list was requested, or the list is
   full and the job is older than its oldest, so it may only have been cut
   off."
  [db gen summaries]
  (let [listed (set (map :result-id summaries))
        oldest (when (>= (count summaries) models/max-open-jobs)
                 (some->> (seq (keep :created-on summaries)) (apply min)))
        gone?  (fn [{:keys [result-id added-gen created-on]}]
                 (and (not (contains? listed result-id))
                      (< (or added-gen 0) gen)
                      (or (nil? oldest)
                          (and created-on (>= created-on oldest)))))]
    (reduce (fn [db held]
              (cond-> db (gone? held) (remove-job (:result-id held))))
            (merge-jobs db summaries)
            (vals (jobs db)))))

(defn fetch-failed
  "The job's result could not be fetched. It keeps its status, the process list
   opens on it offering to fetch it again, and a review waiting for it closes."
  [db result-id]
  (cond-> db
    (job db result-id)
    (-> (assoc-in (conj jobs-path result-id :fetch-failed?) true)
        (open-menu-on result-id))

    (= result-id (get-in db reviewing-path))
    (assoc-in reviewing-path nil)))

(defn set-target
  "The job's result is being applied to the entity `entity-id`, such as the
   transaction process it just created."
  [db result-id entity-id]
  (cond-> db
    (job db result-id)
    (update-in (conj jobs-path result-id) assoc
               :target-id entity-id
               :applying? true)))

(defn start-applying
  "The job's result is being applied onto its target again."
  [db result-id]
  (cond-> db
    (job db result-id)
    (assoc-in (conj jobs-path result-id :applying?) true)))

(defn apply-failed
  "Applying the job's result onto its target failed: it stays listed, offering
   to apply again, with the process list open on it."
  [db result-id]
  (cond-> db
    (job db result-id)
    (-> (update-in (conj jobs-path result-id) dissoc :applying?)
        (open-menu-on result-id))))

(defn remove-job
  "The job is resolved: drop it, and keep it from coming back."
  [db result-id]
  (cond-> (-> (update-in db jobs-path dissoc result-id)
              (update-in fetches-path dissoc result-id)
              (update-in resolved-path (fnil conj #{}) result-id))
    (= result-id (get-in db reviewing-path)) (assoc-in reviewing-path nil)))

(defn resolve-failed
  "Resolving the job failed on the backend: it is unresolved there, so it may
   come back with the next list."
  [db result-id]
  (update-in db resolved-path disj result-id))

(defn by-token
  "The request or job a screen started with `token`, or nil."
  [db token]
  (when token
    (or (get (requests db) token)
        (some (fn [job] (when (= token (:token job)) job))
              (vals (jobs db))))))

(defn processes
  "Every request and job for the process list, newest first. Requests have no
   creation time yet, so they lead."
  [db]
  (concat (vals (requests db))
          (sort-by (fn [job] (- (or (:created-on job) 0)))
                   (vals (jobs db)))))

(defn running-count
  [db]
  (count (filter (comp in-progress-status? :status) (processes db))))

(defn entity-in-progress?
  "Whether a job for the existing entity `entity-id` is running."
  [db entity-id]
  (boolean
   (some (fn [job]
           (and (in-progress-status? (:status job))
                (= entity-id (job-entity-id job))))
         (processes db))))

;; ---------------------------------------------------------------------------
;; Review and the process list
;; ---------------------------------------------------------------------------

(defn reviewing [db] (get-in db reviewing-path))
(defn set-reviewing [db result-id] (assoc-in db reviewing-path result-id))

(defn menu [db] (get-in db menu-path))

(defn set-menu-open
  "Open or close the process list. Closing forgets the highlight."
  [db open?]
  (assoc-in db menu-path (if open? (assoc (menu db) :open? true) {:open? false})))

(defn open-menu-on
  "Open the process list with `result-id` highlighted: its job just finished."
  [db result-id]
  (assoc-in db menu-path {:open? true :highlight result-id}))

(defn open-menu-on-job
  "Open the process list on the job started with `token` alone."
  [db token]
  (assoc-in db menu-path {:open? true :focus token}))

(defn shown-processes
  "The processes the list shows: the focused one alone, else all of them."
  [db]
  (let [focus (:focus (menu db))]
    (cond->> (processes db)
      focus (filter (fn [process] (= focus (:token process)))))))

;; ---------------------------------------------------------------------------
;; Survey answers
;; ---------------------------------------------------------------------------

(defn survey-job
  "The newest finished survey job of the survey process `process-id`, for its
   editor to apply: the one started there, or one left over from a reload."
  [db process-id]
  (->> (vals (jobs db))
       (filter (fn [job]
                 (and (= :survey (job-kind job))
                      (= process-id (job-survey-process-id job))
                      (= :ai.status/completed (:status job)))))
       (sort-by (fn [job] (- (or (:created-on job) 0))))
       first))

(defn older-survey-jobs
  "The ids of the survey jobs of `process-id` started before `result-id`: once
   the editor settles that job's answers, they are superseded."
  [db process-id result-id]
  (let [created-on (or (:created-on (job db result-id)) 0)]
    (->> (vals (jobs db))
         (filter (fn [other]
                   (and (= :survey (job-kind other))
                        (= process-id (job-survey-process-id other))
                        (not= result-id (:result-id other))
                        (<= (or (:created-on other) 0) created-on))))
         (map :result-id))))

(defn survey-applied
  "The answers of `result-id` are in the editor of `process-id`, unsaved."
  [db process-id result-id]
  (assoc-in db (conj survey-applied-path process-id) result-id))

(defn survey-applied-job
  "The job whose answers are in the editor of `process-id`, unsaved, or nil."
  [db process-id]
  (get-in db (conj survey-applied-path process-id)))

(defn survey-settled
  "The editor of `process-id` was left without saving. Returns [db result-id],
   the job whose applied answers that settles, or nil."
  [db process-id]
  [(update-in db survey-applied-path dissoc process-id)
   (survey-applied-job db process-id)])

(defn survey-saved
  "A save of the editor of `process-id` that started while `result-id`'s
   answers were applied succeeded: they are saved. A job applied after the save
   started stays applied, its answers still unsaved."
  [db process-id result-id]
  (cond-> db
    (= result-id (survey-applied-job db process-id))
    (update-in survey-applied-path dissoc process-id)))

(defn result-id-from-payload
  "The ai-enrichment-result id a completion message carries in its subject."
  [payload]
  (first (get-in payload [:completion/subject :entity-ids])))
